import { useState } from 'react'
import { Shield, Loader2, RefreshCw, CheckCircle, Clock, AlertTriangle, XCircle, Play, Eye, History } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useSelfAuditSummary, useSelfAuditQueue, useSelfAuditHistory, useSelfAuditCritical, useRunSelfAudit, useApproveStagedFix, useRejectStagedFix } from '@/hooks/useApi'
import { timeAgo } from '@/lib/utils'

const LEVEL_CONFIG: Record<number, { label: string; color: string; icon: typeof CheckCircle }> = {
  1: { label: 'L1 Auto-fix', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  2: { label: 'L2 Staged', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Clock },
  3: { label: 'L3 Review', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Eye },
  4: { label: 'L4 Critical', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle },
}

export function SelfAuditPanel() {
  const [tab, setTab] = useState<'queue' | 'critical' | 'history'>('queue')
  const [queueFilter, setQueueFilter] = useState('pending')

  const { data: summary, refetch: refetchSummary } = useSelfAuditSummary()
  const { data: queue, isLoading: queueLoading, refetch: refetchQueue } = useSelfAuditQueue(queueFilter)
  const { data: history, isLoading: historyLoading } = useSelfAuditHistory()
  const { data: critical, isLoading: criticalLoading } = useSelfAuditCritical()
  const runMutation = useRunSelfAudit()
  const approveMutation = useApproveStagedFix()
  const rejectMutation = useRejectStagedFix()

  const sum = summary?.summary ?? { auto_fixed: 0, awaiting_review: 0, critical: 0, last_run: null }

  const handleRun = () => {
    runMutation.mutate(undefined, {
      onSuccess: () => { refetchSummary(); refetchQueue(); },
    })
  }

  const handleApprove = (issueId: string) => {
    approveMutation.mutate({ issueId }, { onSuccess: () => refetchQueue() })
  }

  const handleReject = (issueId: string) => {
    rejectMutation.mutate({ issueId }, { onSuccess: () => refetchQueue() })
  }

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Self-Audit Governance
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={handleRun}
                disabled={runMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors disabled:opacity-50"
              >
                {runMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                Run Now
              </button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-surface-elevated border border-border">
              <p className="text-[10px] text-text-muted uppercase">Last Run</p>
              <p className="text-sm text-text-primary font-medium">{sum.last_run ? timeAgo(new Date(sum.last_run)) : 'Never'}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-elevated border border-border">
              <p className="text-[10px] text-text-muted uppercase">Next Run</p>
              <p className="text-sm text-text-primary font-medium">06:00 UTC daily</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-elevated border border-border">
              <p className="text-[10px] text-text-muted uppercase">Auto-Fixed</p>
              <p className="text-sm text-green-400 font-medium">{sum.auto_fixed}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-elevated border border-border">
              <p className="text-[10px] text-text-muted uppercase">Awaiting Review</p>
              <p className="text-sm text-blue-400 font-medium">{sum.awaiting_review}</p>
            </div>
          </div>

          {sum.critical > 0 && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400 flex items-center gap-2 mb-4">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {sum.critical} critical alert{sum.critical > 1 ? 's' : ''} detected
            </div>
          )}

          {runMutation.isSuccess && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-sm text-green-400 mb-4">
              Audit complete: {runMutation.data.level_1_fixed} L1 fixed, {runMutation.data.level_2_found} L2 staged, {runMutation.data.level_4_found} L4 critical
            </div>
          )}

          {runMutation.isError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400 mb-4">
              Audit failed: {runMutation.error instanceof Error ? runMutation.error.message : 'Unknown error'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2">
        {([['queue', 'Fix Queue', Clock], ['critical', 'Critical', AlertTriangle], ['history', 'History', History]] as const).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${tab === key ? 'bg-accent/20 text-accent border-accent/30' : 'text-text-muted border-border hover:border-accent/30'}`}
          >
            <Icon className="h-3 w-3" />
            {label}
          </button>
        ))}
      </div>

      {/* Queue Tab */}
      {tab === 'queue' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4" />
              Level 2 Staged Fixes
              {queueLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
              <div className="ml-auto flex gap-2">
                {['pending', 'approved', 'rejected', 'all'].map((s) => (
                  <button
                    key={s}
                    onClick={() => setQueueFilter(s)}
                    className={`px-2 py-1 text-[10px] rounded border transition-colors ${queueFilter === s ? 'bg-accent/20 text-accent border-accent/30' : 'text-text-muted border-border'}`}
                  >
                    {s}
                  </button>
                ))}
                <button onClick={() => refetchQueue()} className="p-1 text-text-muted hover:text-text-primary"><RefreshCw className="h-3 w-3" /></button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(queue?.items?.length ?? 0) === 0 && !queueLoading && (
              <div className="text-center py-6 text-text-muted text-sm">No staged fixes with status "{queueFilter}"</div>
            )}
            <div className="space-y-2">
              {(queue?.items ?? []).map((item) => (
                <div key={item.issue_id} className="p-3 rounded-lg border border-border bg-surface-elevated space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono text-text-muted">{item.issue_id}</span>
                    <Badge className={`text-[10px] border ${LEVEL_CONFIG[item.level]?.color || ''}`}>{LEVEL_CONFIG[item.level]?.label || 'L' + item.level}</Badge>
                    <Badge variant="outline" className="text-[10px]">{item.status}</Badge>
                    <span className="text-[10px] text-text-muted ml-auto">{item.detected_at ? timeAgo(new Date(item.detected_at)) : ''}</span>
                  </div>
                  <p className="text-sm text-text-primary">{item.description}</p>
                  <div className="text-[10px] text-text-muted">Check: {item.check}</div>
                  {item.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(item.issue_id)}
                        disabled={approveMutation.isPending}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors disabled:opacity-50"
                      >
                        <CheckCircle className="h-3 w-3" /> Approve
                      </button>
                      <button
                        onClick={() => handleReject(item.issue_id)}
                        disabled={rejectMutation.isPending}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
                      >
                        <XCircle className="h-3 w-3" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Critical Tab */}
      {tab === 'critical' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              Level 4 Critical Alerts
              {criticalLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(critical?.alerts?.length ?? 0) === 0 && !criticalLoading && (
              <div className="text-center py-6 text-text-muted text-sm">No critical alerts</div>
            )}
            <div className="space-y-2">
              {(critical?.alerts ?? []).map((alert) => (
                <div key={alert.issue_id} className="p-3 rounded-lg border border-red-500/30 bg-red-500/5 space-y-1">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
                    <span className="text-[10px] font-mono text-text-muted">{alert.issue_id}</span>
                    <span className="text-[10px] text-text-muted ml-auto">{alert.detected_at ? timeAgo(new Date(alert.detected_at)) : ''}</span>
                  </div>
                  <p className="text-sm text-red-400">{alert.description}</p>
                  <div className="text-[10px] text-text-muted">Check: {alert.check}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <History className="h-4 w-4" />
              Audit History
              {historyLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(history?.runs?.length ?? 0) === 0 && !historyLoading && (
              <div className="text-center py-6 text-text-muted text-sm">No audit runs yet</div>
            )}
            <div className="space-y-2">
              {(history?.runs ?? []).map((run) => (
                <div key={run.audit_id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-surface-elevated">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-text-muted">{run.audit_id}</span>
                    <span className="text-[10px] text-text-muted">{run.date ? timeAgo(new Date(run.date)) : ''}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="text-[10px] border bg-green-500/20 text-green-400 border-green-500/30">L1: {run.level_1_fixed}</Badge>
                    <Badge className="text-[10px] border bg-blue-500/20 text-blue-400 border-blue-500/30">L2: {run.level_2_found}</Badge>
                    {run.level_3_found > 0 && <Badge className="text-[10px] border bg-amber-500/20 text-amber-400 border-amber-500/30">L3: {run.level_3_found}</Badge>}
                    {run.level_4_found > 0 && <Badge className="text-[10px] border bg-red-500/20 text-red-400 border-red-500/30">L4: {run.level_4_found}</Badge>}
                    <Badge variant="outline" className="text-[10px]">{run.checks_run} checks</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
